<template>
    <Dialog :visible="visible" :modal="true" :maximizable="true"
            :style="{width: '90vw'}" :contentStyle="{height: '75vh', paddingLeft: '10px'}"
            :header="$t('AppShapefilePreviewDialog.Pré-visualização da importação')"
            @update:visible="$emit('update:visible', $event)"
            @show="onShow" @hide="onHide">

        <div class="preview-body">
            <!-- Mapa -->
            <div class="preview-map-wrapper">
                <div id="shapefile-preview-map" ref="previewMap"></div>
            </div>

            <!-- Controles -->
            <div class="preview-controls">
                <div class="controls-header">
                    <div class="p-field">
                        <label>{{ $t('AppShapefilePreviewDialog.Coluna do nome do talhão') }}</label>
                        <Dropdown v-model="nameColumn" :options="columns" :filter="true"
                                  :placeholder="$t('AppShapefilePreviewDialog.Selecione a coluna')"
                                  appendTo="body" class="w-100"/>
                        <small v-if="submitted && !nameColumn" class="p-error">
                            {{ $t('AppShapefilePreviewDialog.Selecione a coluna do nome') }}
                        </small>
                    </div>
                    <div class="p-field">
                        <label>{{ $t('AppShapefilePreviewDialog.Atributo adicional (opcional)') }}</label>
                        <Dropdown v-model="attributeColumn" :options="columns" :filter="true" :showClear="true"
                                  :placeholder="$t('AppShapefilePreviewDialog.Nenhum')"
                                  appendTo="body" class="w-100"/>
                    </div>
                </div>

                <div class="controls-summary">
                    <span>{{ selectedCount }} / {{ importableCount }}
                        {{ $t('AppShapefilePreviewDialog.selecionados') }}</span>
                    <span v-if="duplicateCount > 0" class="dup-summary">
                        · {{ duplicateCount }} {{ $t('AppShapefilePreviewDialog.já existentes') }}
                    </span>
                </div>

                <DataTable :value="rows" dataKey="index" :scrollable="true" scrollHeight="flex"
                           class="p-datatable-sm preview-table" :rowClass="rowClass"
                           @row-mouseenter="onRowHover">
                    <Column :headerStyle="{width: '3rem'}">
                        <template #header>
                            <Checkbox :binary="true" v-model="allSelected" :disabled="importableCount === 0"
                                      @change="toggleAll"/>
                        </template>
                        <template #body="slotProps">
                            <Checkbox :binary="true" v-model="slotProps.data.selected"
                                      :disabled="slotProps.data.duplicate"
                                      @change="onSelectionChange(slotProps.data)"/>
                        </template>
                    </Column>
                    <Column :header="$t('AppShapefilePreviewDialog.Nome')">
                        <template #body="slotProps">
                            <span>{{ resolvedName(slotProps.data) }}</span>
                            <Tag v-if="slotProps.data.duplicate" severity="warning" class="ml-2"
                                 :value="$t('AppShapefilePreviewDialog.Já existe')"/>
                        </template>
                    </Column>
                    <Column v-if="attributeColumn" :header="$t('AppShapefilePreviewDialog.Atributo')">
                        <template #body="slotProps">
                            {{ slotProps.data.attributes[attributeColumn] }}
                        </template>
                    </Column>
                    <Column :header="$t('AppShapefilePreviewDialog.Área (ha)')" :headerStyle="{width: '7rem'}">
                        <template #body="slotProps">
                            {{ slotProps.data.area }}
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>

        <template #footer>
            <Button :label="$t('AppShapefilePreviewDialog.Cancelar')" icon="pi pi-times" class="p-button-text"
                    :disabled="saving" @click="$emit('update:visible', false)"/>
            <Button :label="importLabel" icon="pi pi-check" :loading="saving"
                    :disabled="saving || selectedCount === 0 || !nameColumn" @click="confirmImport"/>
        </template>
    </Dialog>
</template>

<script>

import gmapsInit from '../../../utils/gmaps';
import * as jsts from 'jsts';

import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';
import Tag from 'primevue/tag';

import GeometryMixin from "@/mixins/GeometryMixin";

const DUPLICATE_COLOR = '#e53935';
const SELECTED_COLOR = '#2e7d32';
const UNSELECTED_COLOR = '#9e9e9e';

export default {
    mixins: [GeometryMixin],
    components: {Dialog, Dropdown, DataTable, Column, Checkbox, Button, Tag},
    props: {
        visible: {type: Boolean, default: false},
        // {attributeColumns: string[], fields: [{index, coordinates, attributes, duplicate, sourceShapefile}]}
        previewData: {type: Object, default: null},
        saving: {type: Boolean, default: false}
    },
    data() {
        return {
            nameColumn: null,
            attributeColumn: null,
            rows: [],
            submitted: false,
            allSelected: false,
            google: null,
            map: null,
            polygons: {}
        }
    },
    computed: {
        columns() {
            if (!this.previewData) {
                return [];
            }
            // backend serializa em snake_case (attribute_columns)
            const cols = this.previewData.attribute_columns || this.previewData.attributeColumns;
            if (Array.isArray(cols) && cols.length > 0) {
                return cols;
            }
            // fallback: deriva as colunas das chaves de attributes das features
            const set = new Set();
            (this.previewData.fields || []).forEach(f => {
                Object.keys(f.attributes || {}).forEach(k => set.add(k));
            });
            return Array.from(set);
        },
        importableCount() {
            return this.rows.filter(r => !r.duplicate).length;
        },
        duplicateCount() {
            return this.rows.filter(r => r.duplicate).length;
        },
        selectedCount() {
            return this.rows.filter(r => r.selected).length;
        },
        importLabel() {
            return `${this.$t('AppShapefilePreviewDialog.Importar')} (${this.selectedCount})`;
        }
    },
    methods: {
        onShow() {
            this.buildRows();
            this.$nextTick(() => this.initMap());
        },
        onHide() {
            this.clearPolygons();
            this.map = null;
            this.submitted = false;
        },
        buildRows() {
            this.submitted = false;
            const columns = this.columns;
            // pré-seleciona a primeira coluna como nome, se ainda não escolhida
            if (!this.nameColumn && columns.length > 0) {
                this.nameColumn = columns[0];
            }
            this.rows = (this.previewData && this.previewData.fields ? this.previewData.fields : [])
                .map(f => ({
                    index: f.index,
                    coordinates: f.coordinates,
                    attributes: f.attributes || {},
                    duplicate: f.duplicate,
                    sourceShapefile: f.source_shapefile || f.sourceShapefile,
                    selected: !f.duplicate,
                    area: '—'
                }));
            this.syncAllSelected();
        },
        resolvedName(row) {
            if (this.nameColumn && row.attributes[this.nameColumn]) {
                return row.attributes[this.nameColumn];
            }
            return this.$t('AppShapefilePreviewDialog.(sem nome)');
        },
        rowClass(row) {
            return row.duplicate ? 'row-duplicate' : '';
        },
        toggleAll() {
            const value = this.allSelected;
            this.rows.forEach(row => {
                if (!row.duplicate) {
                    row.selected = value;
                    this.updatePolygonStyle(row);
                }
            });
        },
        syncAllSelected() {
            const importable = this.rows.filter(r => !r.duplicate);
            this.allSelected = importable.length > 0 && importable.every(r => r.selected);
        },
        onSelectionChange(row) {
            this.updatePolygonStyle(row);
            this.syncAllSelected();
        },
        onRowHover(event) {
            const row = event.data;
            const polygon = this.polygons[row.index];
            if (polygon && this.map) {
                const bounds = new this.google.maps.LatLngBounds();
                polygon.getPaths().forEach(path => path.forEach(latlng => bounds.extend(latlng)));
                this.map.panTo(bounds.getCenter());
            }
        },
        async initMap() {
            try {
                this.google = await gmapsInit();
                const el = this.$refs.previewMap;
                if (!el) {
                    return;
                }
                this.map = new this.google.maps.Map(el, {
                    minZoom: 3, maxZoom: 20, zoom: 4,
                    center: {lat: -14.235, lng: -51.925},
                    mapTypeId: 'hybrid',
                    rotateControl: false, streetViewControl: false, mapTypeControl: false,
                    tilt: 0, disableDefaultUI: true, clickableIcons: false
                });
                this.google.maps.event.trigger(this.map, 'resize');
                this.drawPreview();
            } catch (error) {
                console.error(error);
            }
        },
        drawPreview() {
            this.clearPolygons();
            const reader = new jsts.io.WKTReader();
            const bounds = new this.google.maps.LatLngBounds();
            let hasGeometry = false;

            this.rows.forEach(row => {
                let geometry;
                try {
                    geometry = reader.read(row.coordinates);
                } catch (e) {
                    console.warn('Geometria inválida no preview', row.index, e);
                    return;
                }
                const paths = this.jstsWithHolesToGoogleMaps(geometry, this.google.maps);
                const polygon = new this.google.maps.Polygon({
                    paths: paths,
                    strokeWeight: 2,
                    editable: false,
                    zIndex: 1 / geometry.getArea()
                });
                polygon.setMap(this.map);

                // área em hectares (mesma conta do AppGoogleMapsFields)
                row.area = (this.google.maps.geometry.spherical.computeArea(
                    this.jstsWithoutHolesToGoogleMaps(geometry, this.google.maps)[0]) / 10000).toFixed(2);

                const vm = this;
                this.google.maps.event.addListener(polygon, 'click', function () {
                    if (!row.duplicate) {
                        row.selected = !row.selected;
                        vm.onSelectionChange(row);
                    }
                });

                this.polygons[row.index] = polygon;
                this.applyStyle(polygon, row);

                paths.forEach(path => path.forEach(latlng => {
                    bounds.extend(latlng);
                    hasGeometry = true;
                }));
            });

            if (hasGeometry) {
                this.map.fitBounds(bounds);
            }
        },
        applyStyle(polygon, row) {
            const color = row.duplicate ? DUPLICATE_COLOR : (row.selected ? SELECTED_COLOR : UNSELECTED_COLOR);
            polygon.setOptions({
                strokeColor: color,
                strokeOpacity: 0.9,
                fillColor: color,
                fillOpacity: row.selected ? 0.45 : 0.12
            });
        },
        updatePolygonStyle(row) {
            const polygon = this.polygons[row.index];
            if (polygon) {
                this.applyStyle(polygon, row);
            }
        },
        clearPolygons() {
            Object.values(this.polygons).forEach(p => p.setMap(null));
            this.polygons = {};
        },
        confirmImport() {
            this.submitted = true;
            if (!this.nameColumn || this.selectedCount === 0) {
                return;
            }
            const fields = this.rows.filter(r => r.selected).map(r => ({
                name: r.attributes[this.nameColumn] || '',
                attribute: this.attributeColumn ? (r.attributes[this.attributeColumn] || null) : null,
                coordinates: r.coordinates,
                type: 'PRODUCTIVE'
            }));
            this.$emit('import', {fields});
        }
    }
}
</script>

<style scoped lang="scss">
.preview-body {
    display: flex;
    height: 100%;
    width: 100%;
}

.preview-map-wrapper {
    flex: 1 1 55%;
    min-width: 0;
    height: 100%;
}

#shapefile-preview-map {
    width: 100%;
    height: 100%;
}

.preview-controls {
    flex: 1 1 45%;
    display: flex;
    flex-direction: column;
    padding: 16px;
    min-width: 0;
    overflow: hidden;
}

.controls-header {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
}

.controls-header .p-field {
    flex: 1 1 45%;
    display: flex;
    flex-direction: column;
    margin-bottom: 8px;
}

.controls-header label {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 4px;
    color: #4b4b4b;
}

.w-100 {
    width: 100%;
}

.controls-summary {
    margin: 8px 0;
    font-size: 13px;
    color: #4b4b4b;
}

.dup-summary {
    color: #e53935;
}

.preview-table {
    flex: 1 1 auto;
    min-height: 0;
}

::v-deep .row-duplicate {
    background-color: #fff3f0 !important;
    color: #9e9e9e;
}

.ml-2 {
    margin-left: 8px;
}
</style>
